/**
 * HELIX Professional Design System v9.2.1 (Executive OS)
 *
 * Canonical global theme for HELIX. All UI must reference these tokens.
 * Source: docs/prompts/HELIX-PROFESSIONAL-DESIGN-SYSTEM-v9.2.1.md
 *
 * Design Philosophy:
 * - Professional restraint: muted, sober palette
 * - Decision-grade clarity: crisp hierarchy
 * - Serious, not celebratory: status colors inform, not hype
 * - Evidence-first: visuals reinforce auditability
 */

export const helixTheme = {
  colors: {
    // Backgrounds
    bg: '#FAFBFC',
    bgPure: '#FFFFFF',
    bgSubtle: '#F4F5F7',
    bgMuted: '#EBEEF2',

    // Surfaces
    surface: '#FFFFFF',
    surfaceHover: '#F8F9FA',
    surfaceActive: '#F0F2F5',
    surfaceSelected: '#EDF2FF',

    // Borders
    border: '#E1E4E8',
    borderLight: '#EAECEF',
    borderMedium: '#D0D4DB',
    borderFocus: '#4A6FA5',

    // Text
    text: '#1F2937',
    textSecondary: '#4B5563',
    textTertiary: '#6B7280',
    textMuted: '#9CA3AF',
    textPlaceholder: '#D1D5DB',

    // Primary (Muted Blue)
    primary: '#4A6FA5',
    primaryLight: '#EDF2FF',
    primaryMedium: '#DBEAFE',
    primaryDark: '#3B5A8A',
    primaryDeep: '#3D5F8F',
    primaryMuted: 'rgba(74, 111, 165, 0.125)',

    // Status: Keep / Success
    keep: '#10B981',
    keepLight: '#ECFDF5',
    keepBorder: '#A7F3D0',
    keepDark: '#059669',

    // Status: Eliminate / Error
    eliminate: '#DC2626',
    eliminateLight: '#FEF2F2',
    eliminateBorder: '#FECACA',

    // Status: Defer / Warning
    defer: '#D97706',
    deferLight: '#FFFBEB',
    deferBorder: '#FDE68A',

    // Status: Pending / Neutral
    pending: '#6B7280',
    pendingLight: '#F3F4F6',
    pendingBorder: '#E5E7EB',

    // Accent colors (rare use)
    blue: '#3B82F6',
    blueLight: '#EFF6FF',
    blueBorder: '#BFDBFE',

    indigo: '#6366F1',

    purple: '#7C3AED',
    purpleLight: '#F5F3FF',
    purpleBorder: '#C4B5FD',

    gold: '#B45309',
    goldLight: '#FFF7ED',

    // RAG mapping (for strategic bets, risk indicators)
    green: '#10B981',
    greenLight: '#ECFDF5',
    greenBorder: '#A7F3D0',

    amber: '#D97706',
    amberLight: '#FFFBEB',
    amberBorder: '#FDE68A',

    red: '#DC2626',
    redLight: '#FEF2F2',
    redBorder: '#FECACA',
  },

  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.06), 0 4px 6px -2px rgba(0, 0, 0, 0.03)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
    card: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
    cardHover: '0 4px 12px rgba(0, 0, 0, 0.08)',
    focus: '0 0 0 3px rgba(74, 111, 165, 0.15)',
  },

  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    full: 9999,
  },

  spacing: {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    7: '28px',
    8: '32px',
    10: '40px',
    12: '48px',
  },

  typography: {
    fontFamily: {
      sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      serif: "Georgia, 'Times New Roman', serif",
      mono: "'SF Mono', Monaco, monospace",
    },
    // Back-compat alias used across ui-golden ports.
    fonts: {
      heading: "Georgia, 'Times New Roman', serif",
      body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      mono: "'SF Mono', Monaco, monospace",
    },
    /**
     * Typography Modes
     *
     * EXECUTIVE MODE (default for C-suite/PE/Board users 50-70yo):
     * - Any decision text must be >= 13px
     * - Default body = 14-15px
     * - Tables readable on Zoom at 100% scale
     * - Micro text (<11px) ONLY for tertiary metadata
     */
    modes: {
      // Operate mode: dense UI for power users (analysts, operators)
      operate: {
        h1: 18,
        h2: 16,
        h3: 14,
        bodyLarge: 15,
        body: 13,
        bodySmall: 12,
        micro: 10,
        caption: 11,
        label: 11,
        displayLarge: 28,
        displayMedium: 22,
        displaySmall: 17,
      },
      // Executive mode: readable for C-suite/PE/Board (50-70yo)
      // THIS IS THE DEFAULT FOR HELIX
      executive: {
        pageTitle: 22,      // Page titles: 20-22px
        cardTitle: 16,      // Card titles: 16px
        h1: 22,
        h2: 18,
        h3: 16,
        bodyLarge: 16,
        body: 15,           // Default body: 14-15px
        bodySmall: 14,      // Secondary body: still decision-grade
        secondary: 13,      // Minimum for decision text
        micro: 11,          // Rare use, never for decisions
        caption: 12,
        label: 12,
        displayLarge: 32,
        displayMedium: 26,
        displaySmall: 20,
      },
      // Alias for backward compatibility
      board: {
        pageTitle: 22,
        cardTitle: 16,
        h1: 22,
        h2: 18,
        h3: 16,
        bodyLarge: 16,
        body: 15,
        bodySmall: 14,
        secondary: 13,
        micro: 11,
        caption: 12,
        label: 12,
        displayLarge: 32,
        displayMedium: 26,
        displaySmall: 20,
      },
    },
    /**
     * Table Typography (Executive-readable)
     * - Headers: 12-13px uppercase
     * - Body: 14px for Zoom readability
     * - Row height: 48-50px for comfortable clicking
     */
    table: {
      headerSize: 12,        // Table headers: 12-13px
      bodySize: 14,          // Table body: 14px (Zoom-readable)
      rowHeight: 50,         // Increased from 44px (+6px)
      headerRowHeight: 44,   // Slightly shorter for headers
      cellPaddingY: 14,      // Increased vertical padding
      cellPaddingX: 12,      // Horizontal padding
    },
    /**
     * Badge/Chip Typography
     * - Status badges: 11-12px
     * - Never smaller than 11px
     */
    badge: {
      default: 12,
      small: 11,
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.3,
      normal: 1.5,          // Increased from 1.45 for readability
      relaxed: 1.65,        // Increased from 1.6 for readability
      table: 1.5,           // Explicit for tables
    },
    letterSpacing: {
      tight: '-0.5px',
      normal: '-0.3px',
      wide: '0.3px',
      wider: '0.5px',
      tableHeader: '0.5px', // Uppercase headers need wider spacing
    },
  },

  animation: {
    spring: { type: 'spring', stiffness: 400, damping: 30 },
    smooth: { duration: 0.5, ease: 'easeOut' },
    stagger: (index: number) => ({ delay: index * 0.05 }),
    mode: {
      operate: { enableEntry: true },
      board: { enableEntry: false },
    },
  },
} as const;

// Convenience alias for colors
export const t = helixTheme.colors;

// Convenience alias for typography (executive mode is default)
export const typo = helixTheme.typography.modes.executive;
export const tableTypo = helixTheme.typography.table;
export const badgeTypo = helixTheme.typography.badge;

// Type exports
export type HelixTheme = typeof helixTheme;
export type HelixColors = typeof helixTheme.colors;
export type ColorKey = keyof HelixColors;

// RAG color mapping (for strategic bets)
export const ragColors = {
  green: { dot: t.green, light: t.greenLight, border: t.greenBorder },
  amber: { dot: t.amber, light: t.amberLight, border: t.amberBorder },
  red: { dot: t.red, light: t.redLight, border: t.redBorder },
} as const;

// Severity color mapping (for risk radar)
export const severityColors = {
  critical: { accent: t.eliminate, activeBg: t.eliminate },
  warning: { accent: t.defer, activeBg: t.defer },
  info: { accent: t.blue, activeBg: t.blue },
  ok: { accent: t.keep, activeBg: t.keep },
} as const;

// Urgency color mapping (for decision docket)
export const urgencyColors = {
  critical: { text: t.eliminate, bg: t.eliminateLight },
  high: { text: t.defer, bg: t.deferLight },
  medium: { text: t.blue, bg: t.blueLight },
  low: { text: t.textMuted, bg: t.bgMuted },
} as const;

// Confidence color mapping
export const confidenceColors = {
  high: { bg: t.keepLight, border: t.keepBorder, color: t.keep, label: 'High' },
  medium: { bg: t.deferLight, border: t.deferBorder, color: t.defer, label: 'Medium' },
  low: { bg: t.eliminateLight, border: t.eliminateBorder, color: t.eliminate, label: 'Low' },
} as const;

// Evidence status color mapping
export const evidenceStatusColors = {
  connected: { bg: t.keepLight, border: t.keepBorder, icon: '✓', color: t.keep },
  stale: { bg: t.deferLight, border: t.deferBorder, icon: '△', color: t.defer },
  not_connected: { bg: t.bgMuted, border: t.border, icon: '○', color: t.textMuted },
} as const;

// Status badge colors (keep/eliminate/defer/pending)
export const statusColors = {
  keep: { bg: t.keepLight, fg: t.keep, border: t.keepBorder },
  eliminate: { bg: t.eliminateLight, fg: t.eliminate, border: t.eliminateBorder },
  defer: { bg: t.deferLight, fg: t.defer, border: t.deferBorder },
  pending: { bg: t.pendingLight, fg: t.pending, border: t.pendingBorder },
} as const;
